import { styleCardFooter } from '@/components/pack-page';
import { PackDetailsPaddingPx } from '@/components/pack-page/layout';
import { PlaceholderWhileOffScreen } from '@/components/placeholder-while-offscreen';
import { MediaPackType } from '@/utils/media/types';
import { CardVariant } from '@rhg/components/card/types';
import { defaultCarouselCardSpacing } from '@rhg/components/carousel/utils';
import { dailyPracticeCardWidth } from '@rhg/components/discover-dailies/daily-packs';
import {
    PackCarousel,
    PackCarouselProps,
    PackCarouselSkeleton,
} from '@rhg/components/pack-carousel';
import { SkeletonCardVariant } from '@rhg/components/ui/skeletons';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { usePacks } from '@rhg/hooks/use-packs';
import { Box, Theme, Typography } from '@rhg/libs/material';
import { getValueByBreakpoint, sxAppend } from '@rhg/libs/material/sx';
import { useMemo } from 'react';

export const largeButtonCardWidth = 209;
export const mobileCategoryCardWidth = 120;
export const MAX_CARDS_DISPLAYED = 5;

export interface PacksCarouselSectionProps {
    packClasses?: (string | undefined)[];
    title?: string;
    maxCardLimit?: number;
    cardVariant?: CardVariant;
    skeletonType?: SkeletonCardVariant;
    cardWidth?: PackCarouselProps['itemWidth'];
    packCarouselProps?: Partial<PackCarouselProps>;
}

export const styleTitleRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: (theme: Theme) => theme.ds.spacingUnits.HEADING_CONTENT_DESKTOP,
};

const styleWrapper = {
    maxWidth: '100%',
};
const stylePackCarouselTitle = {
    mb: 2,
};

export const PacksCarouselSection = ({
    packClasses,
    title,
    maxCardLimit,
    cardWidth,
    cardVariant: providedCardVariant = 'carousel',
    skeletonType = 'daily',
    packCarouselProps,
}: PacksCarouselSectionProps) => {
    const currentBreakpoint = useCurrentBreakpointName();
    const { activeCircleId } = useActiveCircleId();
    const { isFetched, packs } = usePacks(
        (packClasses ?? []).filter(
            (packClass): packClass is string => packClass !== undefined
        ),
        activeCircleId
    );

    const ogPack = packs?.[0];

    const pack = useMemo(
        () =>
            ({
                ...ogPack,
                items: ogPack?.items?.slice(
                    0,
                    maxCardLimit ?? MAX_CARDS_DISPLAYED
                ),
            } as MediaPackType),
        [maxCardLimit, ogPack]
    );

    // Need to reverse the order of the items since they are LIFO
    const allPackItems = useMemo(
        () =>
            packs
                ?.reverse()
                .map((pack) => pack?.items)
                .flat(),
        [packs]
    );

    const combinedPack = useMemo(
        () =>
            ({
                ...ogPack,
                items: allPackItems?.slice(
                    0,
                    maxCardLimit ?? MAX_CARDS_DISPLAYED
                ),
            } as MediaPackType),
        [allPackItems, maxCardLimit, ogPack]
    );

    const cardVariant = providedCardVariant;
    const itemWidth = cardWidth ?? dailyPracticeCardWidth;

    return (
        <Box sx={styleWrapper}>
            {title && (
                <Box sx={styleTitleRow}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={stylePackCarouselTitle}
                    >
                        {title}
                    </Typography>
                </Box>
            )}

            <PlaceholderWhileOffScreen
                placeholder={
                    <PackCarouselSkeleton
                        skeletonType={skeletonType}
                        itemWidth={itemWidth}
                        itemSpacing={defaultCarouselCardSpacing}
                    />
                }
            >
                <PackCarousel
                    data-testid="PackCarousel"
                    pack={combinedPack}
                    cardVariant={cardVariant}
                    itemWidth={itemWidth}
                    itemSpacing={defaultCarouselCardSpacing}
                    firstItemOffsetPx={PackDetailsPaddingPx}
                    trackBackButton="DiscoverCarouselBack"
                    trackForwardButton="DiscoverCarouselForward"
                    cardContentSx={styleCardFooter}
                    isFetched={isFetched}
                    skeletonType={skeletonType}
                    allowPackOfPacks
                    {...packCarouselProps}
                    listSx={sxAppend(
                        currentBreakpoint === 'xl'
                            ? {
                                  gridTemplateColumns: `repeat(${
                                      pack?.items?.length
                                  }, minmax(${
                                      getValueByBreakpoint(
                                          itemWidth,
                                          currentBreakpoint
                                      ) ?? 0
                                  }px, 1fr))`,
                                  ':before': {
                                      content: 'unset',
                                  },
                              }
                            : {},
                        packCarouselProps?.listSx
                    )}
                    sx={packCarouselProps?.sx}
                />
            </PlaceholderWhileOffScreen>
        </Box>
    );
};
